import { Paging } from 'components/search/search.hooks';
import omit from 'lodash.omit';
import { getClientAlias } from 'util/client-alias';
import { getClientId } from 'util/client-id';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';
import { getMerchantId } from 'util/merchant-id';
import { incApi } from '../inc-api';
import {
  Admin,
  Client,
  ClientCuisine,
  GuestJourneyBenchmarkCuisine,
  ClientConfiguration,
  ClientDomain,
  clientEmailDomain,
  CreateClientDomainBody,
  CreateClientDomainResponse,
  CreateEmployeeArgs,
  CreateEmployeeResult,
  CreateMerchantBody,
  CreatePromoCodeArgs,
  CreatePromoCodeResult,
  CreateRoleArgs,
  CreateStoreBody,
  emailConfigResponse,
  ListCategoryResult,
  ListEmployeesResult,
  ListMerchantsResult,
  ListRolesResult,
  ListStoreResult,
  ListTimeZonesResult,
  Merchant,
  Module,
  PasswordlessAuthenticationUpdateType,
  PasswordlessFileStatusResponse,
  PromoCode,
  Role,
  SearchPermissionsResult,
  StoreBody,
  StoreResult,
  UpdateClientBody,
  UpdateEmailSettingRequest,
  UpdateEmployeeArgs,
  UpdateRoleArgs,
  WebAppConfig,
  WebAppConfigResponse,
} from './client-domain.types';
import {
  transformAdminRequest,
  transformAdminResponse,
  transformUpdateClientConfig,
} from './client-domain.utils';
import {
  GuestJourneySettings,
  GuestJourneySettingsHistoryEntry,
  GuestJourneySettingsHistoryResponse,
} from 'api/reports-service/guest-journey-reports.types';
import { GuestJourneyStageThresholds } from 'api/reports-service/guest-journey-settings.utils';

/**
 * Served by admin-api itself rather than forwarded under the `incentivio-client-domain`
 * prefix every other url here carries, so this one has none — `chmlnsignature` is the
 * other route shaped like this. admin-api does proxy it on to client-domain, which is
 * why the document is client configuration despite the bare url.
 *
 * `merchantid` and `locationid` are accepted and deliberately not sent. They narrow the
 * document to one merchant or one store, and the home screen is configured once for the
 * whole brand — a per-store answer would gate the builder on a store the operator never
 * chose.
 */
export const webAppConfigUrl = () => `clients/${getClientId()}/webappconfig`;

/**
 * Keeps the four flags the portal gates on and drops the rest of the document.
 *
 * Two reasons, and the second is the one that matters. The portal reads four booleans,
 * so carrying a hundred fields in the store is waste; and the document embeds the
 * client's own configuration, which holds live third-party credentials. Narrowing here
 * means they never reach the store, the devtools, or anything that serialises state.
 *
 * `undefined` is passed through untouched. Mobile answered the absence question
 * separately for each flag, so the difference between "false" and "not there" is
 * information the gates need — see `WebAppConfig`.
 *
 * No body at all becomes `null`, which is a client with no record rather than a client
 * with everything switched off. Callers read that as every gate open, the same way a
 * missing client config does.
 */
export const toWebAppConfig = (
  response?: WebAppConfigResponse | null,
): WebAppConfig | null =>
  response
    ? {
        isLoyaltyCard: response.isLoyaltyCard,
        isEnterCode: response.isEnterCode,
        catering: response.catering,
        giftCard: response.giftCard
          ? { isActive: response.giftCard.isActive }
          : null,
      }
    : null;

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    getAdmin: build.query<Admin, { adminId: string }>({
      query: adminId =>
        `incentivio-client-domain/clients/${getClientId()}/admins/${adminId}`,
      keepUnusedDataFor: 0,
      transformResponse: transformAdminResponse,
    }),

    getClientConfig: build.query<ClientConfiguration, void>({
      query: () => `incentivio-client-domain/clients/${getClientId()}/config`,
      providesTags: ['ClientConfig'],
      transformResponse: (response: ClientConfiguration) => {
        if (!!response?.incentivioPayments?.paymentFee) {
          response.incentivioPayments.paymentFee =
            response.incentivioPayments.paymentFee / 1000;
        }
        return response;
      },
    }),

    /**
     * The client's web app configuration, read for its feature toggles alone. The
     * portal never writes it — the fields here are set elsewhere, and the app home
     * screen only needs to know what they say.
     */
    getWebAppConfig: build.query<WebAppConfig | null, void>({
      query: () => ({
        url: webAppConfigUrl(),
      }),
      providesTags: ['WebAppConfig'],
      transformResponse: toWebAppConfig,
    }),

    updateClientConfig: build.mutation<
      Partial<ClientConfiguration>,
      ClientConfiguration
    >({
      query: preBody => {
        const body = transformUpdateClientConfig(preBody);

        return {
          url: `incentivio-client-domain/clients/${getClientId()}/clientconfig`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['ClientConfig'],
    }),

    listMerchants: build.query<
      ListMerchantsResult,
      { count: number; page: number }
    >({
      query: (params = { count: 1000, page: 0 }) => ({
        url: `incentivio-client-domain/clients/${getClientId()}/merchants`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.merchants,
        element => element.merchantId,
        'Merchant',
      ),
    }),

    createStore: build.mutation<any, CreateStoreBody>({
      query: body => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/merchants/${getMerchantId()}/stores`,
          method: 'POST',
          body: {
            ...body,
            phoneNumber: body.phoneNumber?.substring(2),
            merchant: getMerchantId(),
            storeType: '11c0be0c-efa2-11e3-b939-02cbd19e9b93',
          },
        };
      },
      invalidatesTags: ['Stores'],
    }),

    updateStore: build.mutation<any, { storeId: string; body: StoreBody }>({
      query: ({ storeId, body }) => {
        const di = body.displayInfo[0];

        return {
          url: `incentivio-client-domain/clients/${getClientId()}/merchants/${getMerchantId()}/stores/${storeId}`,
          method: 'PUT',
          body: {
            ...body,
            phoneNumber: body.phoneNumber?.substring(2),
            shortDescription: di.shortDescription,
            longDescription: di.longDescription,
            smallImage: di.smallImage,
            mediumImage: di.mediumImage,
          },
          headers: {
            'Inc-Location-Id': body.storeId,
          },
        };
      },
      invalidatesTags: generateInvalidates(body => body.storeId, 'Store'),
    }),

    getStore: build.query<StoreResult, string>({
      query: storeId => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/merchants/${getMerchantId()}/stores/${storeId}`,
          method: 'GET',
        };
      },
      providesTags: generateProvides(result => result.storeId, 'Store'),
      transformResponse: (response: StoreResult) => ({
        ...response,
        phoneNumber: response.phoneNumber ? `+1${response.phoneNumber}` : null,
        beaconName: undefined,
      }),
    }),

    listStores: build.query<ListStoreResult, { count: number; page: number }>({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}/merchants/${getMerchantId()}/stores`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.stores,
        element => element.storeId,
        'Store',
      ),
    }),

    listAdminStoresOverall: build.query<
      { stores: StoreResult[] },
      void | { merchantid: string }
    >({
      query: params => ({
        url: `incentivio-client-domain/admin/stores/overall`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.stores,
        element => element.storeId,
        'Store',
      ),
    }),

    listRoles: build.query<ListRolesResult, { count: number; page: number }>({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}/searchroles`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.roles,
        element => element.id,
        'Role',
      ),
    }),

    listEmployees: build.query<
      ListEmployeesResult,
      { email?: string; count: number; page: number }
    >({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}/admins`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.admins,
        element => element.adminId,
        'Admin',
      ),
    }),

    listCategories: build.query<
      ListCategoryResult,
      { count: number; page: number }
    >({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}/categories`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.categories,
        element => element.categoryId,
        'Category',
      ),
    }),

    createEmployee: build.mutation<CreateEmployeeResult, CreateEmployeeArgs>({
      query: body => ({
        url: `incentivio-client-domain/clients/${getClientId()}/admins`,
        method: 'POST',
        body: transformAdminRequest(body),
      }),
      invalidatesTags: ['Admins'],
    }),

    updateEmployee: build.mutation<any, UpdateEmployeeArgs>({
      query: body => ({
        url: `incentivio-client-domain/clients/${getClientId()}/admins/${
          body.adminId
        }`,
        method: 'PUT',
        body: transformAdminRequest(body),
      }),
      invalidatesTags: generateInvalidates(body => body.adminId, 'Admin'),
    }),

    deleteEmployee: build.mutation<any, { adminId: string }>({
      query: body => ({
        url: `incentivio-client-domain/clients/${getClientId()}/admins/${
          body.adminId
        }`,
        method: 'DELETE',
        body: {
          adminId: body.adminId,
        },
      }),
      invalidatesTags: ['Admins'],
    }),

    findRole: build.query<Role, string>({
      query: id => ({
        url: `incentivio-client-domain/clients/${getClientId()}/findrole/${id}`,
      }),
      providesTags: generateProvides(result => result.id, 'Role'),
      transformResponse: res => {
        return {
          ...res,
          description: res.description ?? '',
        };
      },
    }),

    updateRole: build.mutation<null, UpdateRoleArgs>({
      query: body => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/updaterole`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: generateInvalidates(body => body.id, 'Role'),
    }),

    createRole: build.mutation<{ id: string }, CreateRoleArgs>({
      query: body => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/createrole`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Roles'],
    }),

    deleteRole: build.mutation<null, { id: string }>({
      query: id => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/deleterole/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Roles'],
    }),

    searchPermissions: build.query<SearchPermissionsResult, void>({
      query: () => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/searchpermissions`,
        };
      },
      transformResponse: res => {
        return {
          ...res,
          modules: res.modules
            .filter((module: Module) => module.allPermissions.length > 0)
            .sort((a: Module, b: Module) => a.order - b.order),
        };
      },
    }),

    listTimezones: build.query<ListTimeZonesResult, any>({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}/timezones`,
        params,
      }),
      providesTags: ['Timezones'],
    }),

    getClient: build.query<Client, any>({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}`,
        params,
      }),
      providesTags: ['Client'],
    }),

    updateClient: build.mutation<any, UpdateClientBody>({
      query: body => ({
        url: `incentivio-client-domain/clients/${getClientId()}`,
        method: 'PUT',
        body: omit(body, 'clientId'),
      }),
      invalidatesTags: () => ['Client'],
    }),

    resetPassword: build.mutation({
      query: email => ({
        url: `incentivio-client-domain/clients/${getClientAlias()}/admins/${email}/requestpwreset`,
        method: 'POST',
      }),
    }),

    verifyPwResetRequest: build.query({
      query: id => ({
        url: `incentivio-client-domain/verifypwresetrequest?id=${id}`,
      }),
    }),

    resetPasswordSubmit: build.mutation({
      query: body => ({
        url: `incentivio-client-domain/resetpassword`,
        method: 'POST',
        body,
      }),
    }),

    listPersonas: build.query({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}/personas`,
        params: {
          ...params,
          merchantid: getMerchantId(),
        },
      }),
    }),

    listPromoCodes: build.query<{ promoCodes: PromoCode[] } & Paging, any>({
      query: params => ({
        url: `incentivio-client-domain/clients/${getClientId()}/promocode`,
        method: 'GET',
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.promoCodes,
        element => element.promoCodeId,
        'PromoCode',
      ),
    }),
    createPromoCode: build.mutation<CreatePromoCodeResult, CreatePromoCodeArgs>(
      {
        query: body => {
          const { ...rest } = body;

          return {
            url: `incentivio-client-domain/clients/${getClientId()}/promocode`,
            method: 'POST',
            body: {
              ...rest,
              title: rest.code,
              longDescription: rest.shortDescription,
              merchants: [getMerchantId()],
            },
          };
        },
        invalidatesTags: ['PromoCodes'],
      },
    ),
    deletePromoCode: build.mutation<any, string>({
      query: promoCodeId => ({
        url: `incentivio-client-domain/clients/${getClientId()}/promocode/${promoCodeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PromoCodes'],
    }),
    updatePromoCode: build.mutation<any, PromoCode>({
      query: body => {
        const { ...rest } = body;
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/promocode/${
            body.promoCodeId
          }`,
          method: 'PUT',
          body: {
            ...rest,
            title: rest.code,
            longDescription: rest.shortDescription,
          },
        };
      },
      invalidatesTags: generateInvalidates(
        body => body.promoCodeId,
        'PromoCode',
      ),
    }),
    getPromoCode: build.query<PromoCode, string>({
      query: promoCodeId => ({
        url: `incentivio-client-domain/clients/${getClientId()}/promocode/${promoCodeId}`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.promoCodeId, 'PromoCode'),
    }),
    getEmailConfig: build.query<emailConfigResponse, null>({
      query: () => ({
        url: `incentivio-client-domain/clients/email-config`,
        method: 'GET',
      }),
      providesTags: ['ClientEmailConfig'],
      transformResponse: (
        response: emailConfigResponse,
      ): emailConfigResponse => {
        const removeDomain = (email: string) => {
          const atIndex = email.indexOf('@');
          return atIndex !== -1 ? email.substring(0, atIndex) : email;
        };

        return {
          ...response,
          fromEmail: response.fromEmail
            ? removeDomain(response.fromEmail)
            : response.fromEmail,
          replyTo: response.replyTo
            ? removeDomain(response.replyTo)
            : response.replyTo,
        };
      },
    }),
    clientConfigEmail: build.mutation<any, Partial<UpdateEmailSettingRequest>>({
      query: body => {
        return {
          url: `incentivio-client-domain/clients/email-config`,
          method: 'PUT',
          body: {
            ...body,
            externalDomainId: body.activeDomainId,
          },
        };
      },
      invalidatesTags: ['ClientEmailConfig'],
    }),

    updatePasswordlessConfig: build.mutation<
      PasswordlessAuthenticationUpdateType,
      PasswordlessAuthenticationUpdateType
    >({
      query: body => {
        return {
          url: `incentivio-client-domain/passwordless-authentication-settings`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['ClientConfig'],
    }),

    getPasswordlessFileStatus: build.query<
      PasswordlessFileStatusResponse,
      void
    >({
      query: () => ({
        url: `incentivio-client-domain/passwordless-authentication-settings/file-status`,
        method: 'GET',
      }),
    }),

    addClientEmailDomain: build.mutation<
      null,
      Pick<clientEmailDomain, 'domain'>
    >({
      query: ({ domain }) => {
        return {
          url: `incentivio-client-domain/clients/email-config/client-domain?domain-name=${domain}`,
          method: 'POST',
        };
      },
    }),
    verifyEmailDomain: build.mutation<any, Pick<clientEmailDomain, 'id'>>({
      query: ({ id }) => {
        return {
          url: `incentivio-client-domain/clients/email-config/verify-domain?domain-id=${id}`,
          method: 'POST',
        };
      },
      invalidatesTags: ['ClientEmailConfig'],
    }),

    getMerchant: build.query<Merchant, string>({
      query: merchantId => ({
        url: `incentivio-client-domain/clients/${getClientId()}/merchants/${merchantId}`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.merchantId, 'Merchant'),
    }),

    createMerchant: build.mutation<any, CreateMerchantBody>({
      query: body => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/merchants`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Merchants'],
    }),

    updateMerchant: build.mutation<any, CreateMerchantBody>({
      query: body => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/merchants/${getMerchantId()}`,
          method: 'PUT',
          body: {
            ...body,
            merchantId: getMerchantId(),
          },
        };
      },
      invalidatesTags: generateInvalidates(() => getMerchantId(), 'Merchant'),
    }),

    verifyAdminEmail: build.query<any, { code: string }>({
      query: ({ code }) => {
        return {
          url: `incentivio-client-domain/verifyemail?code=${encodeURIComponent(
            code,
          )}`,
          method: 'GET',
        };
      },
    }),

    verifyEmployee: build.mutation<void, string>({
      query: adminId => {
        return {
          url: `incentivio-client-domain/clients/${getClientId()}/admins/${adminId}/verify`,
          method: 'POST',
        };
      },
    }),

    createClientDomain: build.mutation<
      CreateClientDomainResponse,
      CreateClientDomainBody
    >({
      query: body => {
        return {
          url: `incentivio-client-domain/clientdomain`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['ClientDomain'],
    }),

    deleteClientDomain: build.mutation<void, string>({
      query: id => {
        return {
          url: `incentivio-client-domain/clientdomain/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['ClientDomain'],
    }),

    getClientDomainsByClientId: build.query<ClientDomain[], void>({
      query: () => {
        return {
          url: `incentivio-client-domain/clientdomain`,
          method: 'GET',
        };
      },
      providesTags: ['ClientDomain'],
    }),

    cancelClientDomain: build.mutation<void, string>({
      query: id => {
        return {
          url: `incentivio-client-domain/clientdomain/${id}/cancel`,
          method: 'POST',
        };
      },
      invalidatesTags: ['ClientDomain'],
    }),

    getClientDomain: build.query<ClientDomain, string>({
      query: id => {
        return {
          url: `incentivio-client-domain/clientdomain/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['ClientDomain'],
    }),

    // Guest Journey cuisine (Settings spec v0.2) — the cuisine used as the
    // Benchmarks comparison baseline, which seeds the *required* `benchmarkCuisine`
    // filter default (fetched up front by the Benchmarks bootstrap so the filter
    // has a valid synchronous default). Also carries the cuisine-derived
    // `churnBoundaryDays` that replaced the old client-configurable churn window.
    // Client resolved from auth context — no `clients/{id}` path segment.
    getClientCuisine: build.query<ClientCuisine, void>({
      query: () => ({
        url: `incentivio-client-domain/guest-journey/cuisine`,
        method: 'GET',
      }),
    }),

    // The cuisine categories the brand can benchmark against — drives the Guest
    // Journey Benchmarks "Benchmark against" filter options (sc-371637). Distinct
    // from `getClientCuisine` above (the client's *own* cuisine); this is the
    // full list to compare against, `{ displayLabel, cuisine }` per option: the
    // filter renders `displayLabel` and sends `cuisine` as `benchmarkCuisine`.
    // Moved here from the reports service per BE. Array-guarded so a non-array
    // body can't crash the option `.map`.
    getClientCuisineList: build.query<GuestJourneyBenchmarkCuisine[], void>({
      query: () => ({
        url: `incentivio-client-domain/clients/cuisine/list`,
        method: 'GET',
      }),
      transformResponse: (cuisines: GuestJourneyBenchmarkCuisine[]) =>
        Array.isArray(cuisines) ? cuisines : [],
    }),

    // Guest Journey settings — the per-client stage taxonomy (tier labels +
    // thresholds). Migrated out of the reports service into the Client module in
    // API v0.6 (spec `guest-journey-settings-api-v0.2`). No `clients/{id}` path
    // segment: the BE resolves the client from the auth context (see the v0.6
    // reconciliation §0.4). Single source of every tier name across the report —
    // see the settings utils.
    getGuestJourneySettings: build.query<GuestJourneySettings, void>({
      query: () => ({
        url: `incentivio-client-domain/guest-journey/settings`,
        method: 'GET',
      }),
    }),

    // Platform-recommended default stage boundaries — the Settings "Default
    // Thresholds" card, slider ticks, and "Reset to Defaults". Distinct from
    // getGuestJourneySettings (the client's saved values).
    getGuestJourneySettingsDefaults: build.query<
      GuestJourneyStageThresholds,
      void
    >({
      query: () => ({
        url: `incentivio-client-domain/guest-journey/settings/defaults`,
        method: 'GET',
      }),
    }),

    // Persist edited stage thresholds + tier names from the Settings page. The
    // saved settings are echoed back so the cached `getGuestJourneySettings`
    // result is patched in place — every tab re-renders with the new taxonomy
    // without an extra round-trip. The BE derives the acting admin from auth, so
    // no `USERID` header is sent (§0.3).
    updateGuestJourneySettings: build.mutation<
      GuestJourneySettings,
      GuestJourneySettings
    >({
      query: body => ({
        url: `incentivio-client-domain/guest-journey/settings`,
        method: 'POST',
        body,
      }),
      // The report renders settings-driven stage labels, so refetch it after a
      // save — the cached response otherwise keeps the old names (sc-374376).
      invalidatesTags: ['GuestJourneyReport'],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          endpoints.util.updateQueryData(
            'getGuestJourneySettings',
            undefined,
            () => data,
          ),
        );
      },
    }),

    // Revert this client's visit/spend thresholds to the platform defaults,
    // server-side. The BE keeps the tier names, records a settings-history entry,
    // and echoes the updated settings back — patched into the cached
    // `getGuestJourneySettings` so every tab re-renders with the reset values.
    resetGuestJourneySettings: build.mutation<GuestJourneySettings, void>({
      query: () => ({
        url: `incentivio-client-domain/guest-journey/settings/reset`,
        method: 'POST',
      }),
      // Refetch the report so it reflects the reset thresholds (sc-374376).
      invalidatesTags: ['GuestJourneyReport'],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          endpoints.util.updateQueryData(
            'getGuestJourneySettings',
            undefined,
            () => data,
          ),
        );
      },
    }),

    // Audit trail of prior settings saves, shown by the "View Changes" toggle.
    // The BE returns a bare `GuestJourneySettingsHistoryEntryDTO[]` (newest
    // first); wrap it so the history table reads `data.entries`.
    getGuestJourneySettingsHistory: build.query<
      GuestJourneySettingsHistoryResponse,
      void
    >({
      query: () => ({
        url: `incentivio-client-domain/guest-journey/settings/history`,
        method: 'GET',
      }),
      transformResponse: (entries: GuestJourneySettingsHistoryEntry[]) => ({
        entries,
      }),
    }),
  }),
});

export const {
  useGetAdminQuery,
  useLazyGetAdminQuery,
  useGetClientConfigQuery,
  useLazyGetClientConfigQuery,
  useUpdateClientConfigMutation,
  useLazyListMerchantsQuery,
  useListMerchantsQuery,
  useListStoresQuery,
  useLazyListStoresQuery,
  useLazyListAdminStoresOverallQuery,
  useListRolesQuery,
  useFindRoleQuery,
  useUpdateRoleMutation,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useSearchPermissionsQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useGetStoreQuery,
  useLazyGetStoreQuery,
  useListEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useListCategoriesQuery,
  useListTimezonesQuery,
  useGetClientQuery,
  useUpdateClientMutation,
  useResetPasswordMutation,
  useVerifyPwResetRequestQuery,
  useResetPasswordSubmitMutation,
  useListPromoCodesQuery,
  useCreatePromoCodeMutation,
  useDeletePromoCodeMutation,
  useUpdatePromoCodeMutation,
  useGetPromoCodeQuery,
  useListPersonasQuery,
  useGetEmailConfigQuery,
  useClientConfigEmailMutation,
  useAddClientEmailDomainMutation,
  useVerifyEmailDomainMutation,
  useGetMerchantQuery,
  useCreateMerchantMutation,
  useUpdateMerchantMutation,
  useLazyVerifyAdminEmailQuery,
  useVerifyEmployeeMutation,
  useCreateClientDomainMutation,
  useDeleteClientDomainMutation,
  useGetClientDomainsByClientIdQuery,
  useCancelClientDomainMutation,
  useGetClientDomainQuery,
  useGetClientCuisineQuery,
  useGetClientCuisineListQuery,
  useGetGuestJourneySettingsQuery,
  useGetGuestJourneySettingsDefaultsQuery,
  useResetGuestJourneySettingsMutation,
  useLazyGetGuestJourneySettingsQuery,
  useUpdateGuestJourneySettingsMutation,
  useGetGuestJourneySettingsHistoryQuery,
  useUpdatePasswordlessConfigMutation,
  useGetPasswordlessFileStatusQuery,
  useGetWebAppConfigQuery,
} = endpoints;
